// Bounded-concurrency upload queue with per-file progress. Used by the media gallery so
// large multi-file uploads stream (via api.uploadWithProgress / XHR) a few at a time
// instead of buffering everything at once and freezing the tab. Domain-agnostic: the
// caller supplies how to turn a file + its read metadata into a POST path.
import { api } from './api';
import { readMediaMeta, isSupportedMedia, type MediaMeta } from './media-meta';

export type UploadStatus = 'queued' | 'preparing' | 'uploading' | 'done' | 'error' | 'cancelled';

export interface UploadTask {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number; // 0..1 (upload fraction)
  error?: string;
  abort?: () => void;
  context?: unknown; // caller-supplied per-batch context (e.g. the target album id), captured at add() time
}

const isActive = (s: UploadStatus) => s === 'queued' || s === 'preparing' || s === 'uploading';

let seq = 0;

export interface UploadQueueOptions {
  /** Build the POST path (including any query string) for a file once its metadata is read.
   *  `context` is whatever was passed to add() for this batch (captured at enqueue time). */
  buildPath: (file: File, meta: MediaMeta, context: unknown) => string;
  /** Called on every state change so the host can re-render. */
  onChange: () => void;
  /** Optional follow-up after a file's main upload succeeds — e.g. upload a generated
   *  thumbnail. Receives the upload's parsed response (the created record). Non-fatal:
   *  failures here are swallowed so the item still counts as uploaded. */
  onUploaded?: (result: unknown, file: File, meta: MediaMeta, context: unknown) => Promise<void>;
  /** Called once when the queue drains after having had active work (e.g. to refresh a list). */
  onBatchComplete?: () => void;
  /** Max simultaneous uploads. Default 3 — keeps memory/bandwidth sane for big files. */
  concurrency?: number;
  /** Rejected-file notice (unsupported type). */
  onUnsupported?: (file: File) => void;
}

export class UploadQueue {
  tasks: UploadTask[] = [];
  private readonly opts: UploadQueueOptions;
  private readonly concurrency: number;
  private running = 0;
  private hadWork = false;

  constructor(opts: UploadQueueOptions) {
    this.opts = opts;
    this.concurrency = Math.max(1, opts.concurrency ?? 3);
  }

  get activeCount(): number { return this.tasks.filter((t) => isActive(t.status)).length; }
  get hasFinished(): boolean { return this.tasks.some((t) => !isActive(t.status)); }

  /** Enqueue files (filtering unsupported types) and start pumping. `context` is carried
   *  through to buildPath for every file in this batch (e.g. the target album id). */
  add(files: File[], context?: unknown): void {
    for (const file of files) {
      if (!isSupportedMedia(file)) { this.opts.onUnsupported?.(file); continue; }
      this.tasks = [...this.tasks, { id: `up_${++seq}`, file, status: 'queued', progress: 0, context }];
    }
    this.opts.onChange();
    this.pump();
  }

  cancel(id: string): void {
    const t = this.tasks.find((x) => x.id === id);
    if (!t) return;
    if (t.status === 'uploading') t.abort?.(); // onabort path flips it to 'cancelled'
    else if (isActive(t.status)) { t.status = 'cancelled'; this.opts.onChange(); }
  }

  retry(id: string): void {
    const t = this.tasks.find((x) => x.id === id);
    if (!t || isActive(t.status)) return;
    t.status = 'queued'; t.progress = 0; t.error = undefined;
    this.opts.onChange();
    this.pump();
  }

  /** Drop finished (done/error/cancelled) tasks from the list. */
  clearFinished(): void {
    this.tasks = this.tasks.filter((t) => isActive(t.status));
    this.opts.onChange();
  }

  private pump(): void {
    while (this.running < this.concurrency) {
      const next = this.tasks.find((t) => t.status === 'queued');
      if (!next) break;
      this.run(next);
    }
  }

  private async run(task: UploadTask): Promise<void> {
    this.running++;
    this.hadWork = true;
    try {
      task.status = 'preparing';
      this.opts.onChange();
      const meta = await readMediaMeta(task.file);
      if ((task.status as UploadStatus) === 'cancelled') return; // cancelled while reading
      const path = this.opts.buildPath(task.file, meta, task.context);
      task.status = 'uploading';
      this.opts.onChange();
      const handle = api.uploadWithProgress<unknown>(path, task.file, (f) => { task.progress = f; this.opts.onChange(); });
      task.abort = handle.abort;
      const result = await handle.promise;
      task.abort = undefined;
      // Follow-up (e.g. thumbnail) runs while the task still reads as in-progress, so the
      // batch-complete refresh below only fires once thumbnails are in place.
      if (this.opts.onUploaded) { try { await this.opts.onUploaded(result, task.file, meta, task.context); } catch { /* non-fatal */ } }
      task.status = 'done'; task.progress = 1;
    } catch (e) {
      const err = e as { code?: string; message?: string };
      task.status = err?.code === 'ABORTED' ? 'cancelled' : 'error';
      if (task.status === 'error') task.error = err?.message || 'Upload failed';
    } finally {
      task.abort = undefined;
      this.running--;
      this.opts.onChange();
      this.pump();
      if (this.running === 0 && !this.tasks.some((t) => t.status === 'queued') && this.hadWork) {
        this.hadWork = false;
        this.opts.onBatchComplete?.();
      }
    }
  }
}
